/**
 * useQuery hook for the publication folders tree (used in CopyFolderModal /
 * CopyStoriesModal to let users pick a destination publication).
 *
 * Replaces `storyListEffects.getPublicationFoldersTree` (compose-app). Mirrors
 * the same `getAllPublicationsFolderHierarchy` Hasura query, building the
 * hierarchy map with `buildPublicationFolderHierarchyMap` and shaping sub-folders
 * recursively with `mapPublicationSubFoldersRecursively`.
 *
 * Auth note (§1): non-admin reachable → `postQuery`, not `useGraphQLQuery`.
 *
 * The retired effect dispatched `storyListActionCreators.setPublicationFoldersTree`.
 * Callers now consume the hook's `data` directly.
 *
 * Pass `null` to disable during route transitions before `entityId > 0` (§9).
 */

import { useQuery } from '@tanstack/react-query';
import { getAllPublicationsFolderHierarchy } from '@opengov/stories-shared/graphQL';
import { buildPublicationFolderHierarchyMap } from '@opengov/stories-shared/utils';

import { postQuery } from '../../../effects/publications/postQuery';
import { mapPublicationSubFoldersRecursively } from '../../../utils';
import { useToast } from '../../Toast/context/useToast.context';
import { IPublicationFolder } from '../../../../types/storyListTypes';
import { storyListQueryKeys, PublicationFoldersTreeKeyParams } from './storyListQueryKeys';

export type UsePublicationFoldersTreeParams = PublicationFoldersTreeKeyParams | null;

export function usePublicationFoldersTree(
  params: UsePublicationFoldersTreeParams,
  errorMessage: string
) {
  const { toggleToast } = useToast();

  return useQuery<IPublicationFolder[], Error>({
    queryKey: params
      ? storyListQueryKeys.publicationFoldersTree(params)
      : ([...storyListQueryKeys.all, 'publicationFoldersTree', 'disabled'] as const),
    queryFn: async () => {
      if (!params) throw new Error('Disabled');
      const { entityId } = params;

      const response = await postQuery(getAllPublicationsFolderHierarchy, { entityId });
      const publicationFolderHierarchy = buildPublicationFolderHierarchyMap(
        response.publicationFolders.map((folder) => folder)
      );
      return Object.values(publicationFolderHierarchy)
        .filter((folder) => !!folder.publication)
        .map((folder) => ({
          storyFolder: {
            id: folder.id,
            title: folder.title,
            displayName: folder.title,
            publication: folder.publication,
            subFolders: mapPublicationSubFoldersRecursively(folder.subFolders),
          },
        })) as IPublicationFolder[];
    },
    enabled: params !== null,
    onError: () => {
      toggleToast({ title: errorMessage, severity: 'error' });
    }
  });
}
